<template>
  <h1 class="header-text">{{ $t(isRedeemVoucher ? 'processEvent.voucher.header' : 'processEvent.buy.header') }}</h1>

  <form @submit.prevent="next()" v-if="!isRedeemVoucherDone">
    <phone-number-selector
      :selectedCountry="country"
      :phoneNumber="phoneNumber"
      @update:selectedCountry="selectCountry"
      @update:phoneNumber="changePhoneNumber"
      :invalid="invalidPhonenumber"
    />
    <voucher-card :expanded="isRedeemVoucher" :invalid="invalidVoucherCode" v-model="voucherCode" />
    <button
      v-if="isRedeemVoucher || (packs === null && !isPhonePrefilled)"
      class="submit-phonenumber"
      :disabled="(isRedeemVoucher && processLoading) || isLoading"
    >
      <span v-if="!(isRedeemVoucher && processLoading)">{{ $t('next') }}</span>
      <span class="spinner">
        <spinner-component v-if="(isRedeemVoucher && processLoading) || isLoading" />
      </span>
    </button>
    <button class="secondary-btn" type="button" @click="changeProcessEvent()">
      {{ $t(isRedeemVoucher ? 'processEvent.buy.subtext' : 'processEvent.voucher.subtext') }}
    </button>
  </form>
  <template v-if="isRedeemVoucher && isRedeemVoucherDone">
    <voucher-success-card
      :isSuccess="redeemStatus"
      @showVoucher="isRedeemVoucherDone = false"
      :phoneNumber="fullPhoneNumber"
      :voucherSuccess="voucherResponse"
    />
  </template>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, PropType } from 'vue';
import PhoneNumberSelector from '@/components/PhoneNumberSelector.vue';
import VoucherSuccessCard from '@/components/VoucherSuccessCard.vue';
import VoucherCard from '@/components/VoucherCard.vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import { redeemVoucher } from '@/api/vouchers';
import { NamedCountry } from '@/models/namedcountry';
import { VoucherResponse } from '@/types/vouchers';
import { PurchasePacks } from '@/types/products';
import { TrackEvents } from '@/types/trackEvents';
import { useMixpanel } from '@/composables';

enum ProcessEvent {
  BUY_CREDIT = 'buy_credit',
  REDEEM_VOUCHER = 'redeem_voucher',
}

export default defineComponent({
  name: 'BuyCreditOrRedeemVoucherForm',
  props: {
    country: {
      type: Object as PropType<NamedCountry>,
      required: true,
    },
    isPhonePrefilled: {
      type: Boolean,
      required: true,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    fullPhoneNumber: {
      type: String,
      required: true,
    },
    invalidPhonenumber: {
      type: Boolean,
      required: false,
    },
    packs: {
      type: [null, Object] as PropType<null | PurchasePacks>,
      required: false,
      default: null,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    PhoneNumberSelector,
    VoucherCard,
    VoucherSuccessCard,
    SpinnerComponent,
  },
  emits: ['update:selectedCountry', 'update:phoneNumber', 'update:purchasePack', 'submit'],
  setup(props, { emit }) {
    const { track } = useMixpanel();

    const invalidVoucherCode = ref<boolean>(false);
    const processEvent = ref<ProcessEvent>(ProcessEvent.BUY_CREDIT);
    const isRedeemVoucherDone = ref<boolean>(false);
    const redeemStatus = ref<boolean>(false);
    const processLoading = ref<boolean>(false);
    const voucherCode = ref<string>();
    const voucherResponse = ref<VoucherResponse | null>(null);

    const isRedeemVoucher = computed<boolean>(() => processEvent.value === ProcessEvent.REDEEM_VOUCHER);

    onMounted(() => {
      if (document.querySelector('#shop-component')?.attributes.getNamedItem('show')?.value === 'voucher') {
        processEvent.value = ProcessEvent.REDEEM_VOUCHER;
      }
      track(TrackEvents.PHONE_NUMBER_ENTRY_VIEW);
    });

    const handleRedeemVoucher = async () => {
      invalidVoucherCode.value = false;

      if (!voucherCode.value) {
        invalidVoucherCode.value = true;
        return;
      }
      if (isRedeemVoucher.value && processLoading.value) return;
      voucherResponse.value = null;
      processLoading.value = true;
      try {
        const res = await redeemVoucher(voucherCode.value as string, props.fullPhoneNumber);
        voucherResponse.value = res;
        redeemStatus.value = true;
        voucherCode.value = '';
        track(TrackEvents.REDEEM_VOUCHER_SUCCESS);
      } catch (e) {
        redeemStatus.value = false;
        track(TrackEvents.REDEEM_VOUCHER_FAILURE);
      } finally {
        processLoading.value = false;
        isRedeemVoucherDone.value = true;
      }
    };

    const changePhoneNumber = (phonenumber: string) => {
      emit('update:phoneNumber', phonenumber);
    };

    const selectCountry = (country: NamedCountry) => {
      emit('update:selectedCountry', country);
    };

    const next = () => {
      if (isRedeemVoucher.value) {
        handleRedeemVoucher();
        return;
      }
      if (props.packs === null) {
        emit('update:purchasePack');
      } else {
        emit('submit');
      }
    };

    const changeProcessEvent = () => {
      processEvent.value =
        processEvent.value === ProcessEvent.BUY_CREDIT ? ProcessEvent.REDEEM_VOUCHER : ProcessEvent.BUY_CREDIT;
    };

    return {
      redeemStatus,
      isRedeemVoucherDone,
      isRedeemVoucher,
      voucherCode,
      voucherResponse,
      invalidVoucherCode,
      processLoading,
      next,
      changeProcessEvent,
      changePhoneNumber,
      selectCountry,
    };
  },
});
</script>

<style lang="scss">
.header-text {
  @extend %label;
  font-size: 1.5rem !important;
  margin-bottom: 1rem !important;
}
.submit-phonenumber {
  position: relative;

  &:disabled {
    background: lighten($color: $color-primary, $amount: 10) !important;
  }

  .spinner {
    position: absolute !important;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
